/**
 * The launch cities — one list, three consumers.
 *
 * There were three lists before this, and they disagreed. landing.html
 * advertises sixteen cities in four places (the list, the map pins and both
 * "choose your city" dropdowns). App.js's CITY_SLUGS held seven, which is what
 * decides whether /events/<something> renders a city page or an event page.
 * CityEventsPage held a third list of eight, for display names.
 *
 * The consequence was not cosmetic. A slug missing from CITY_SLUGS falls
 * through to EventDetailPage, which looks for an event with that id, does not
 * find one, and renders "Event not found". So nine of the sixteen cities on the
 * marketing page — Dallas, Minneapolis, Los Angeles, Philadelphia, Chicago,
 * Boston, Columbus, Seattle, Newark, Charlotte — answered a dead end to anyone
 * who typed or shared them. Miami had the opposite problem: a working page for
 * a city the marketing never mentions.
 *
 * Adding a city now means adding one row here. citiesSpec.test.js fails if the
 * landing page ever advertises a city this list does not carry, so the two
 * cannot drift apart again without something going red.
 */

/** slug → the name shown on the page. Order matches landing.html. */
export const CITIES = Object.freeze({
  atlanta: 'Atlanta',
  'washington-dc': 'Washington DC',
  'new-york': 'New York',
  houston: 'Houston',
  'dallas-fort-worth': 'Dallas–Fort Worth',
  minneapolis: 'Minneapolis',
  'los-angeles': 'Los Angeles',
  philadelphia: 'Philadelphia',
  chicago: 'Chicago',
  boston: 'Boston',
  columbus: 'Columbus',
  seattle: 'Seattle',
  newark: 'Newark',
  charlotte: 'Charlotte',
  toronto: 'Toronto',
  montreal: 'Montreal',

  // Added with the map's second ring of pins. A pin on the landing page is an
  // advert, and cities.test.js fails if one has no page to land on.
  baltimore: 'Baltimore',
  detroit: 'Detroit',
  raleigh: 'Raleigh',
  nashville: 'Nashville',
  denver: 'Denver',
  'salt-lake-city': 'Salt Lake City',
  phoenix: 'Phoenix',
  'san-diego': 'San Diego',
  'san-francisco': 'San Francisco',
  ottawa: 'Ottawa',
  vancouver: 'Vancouver',
  calgary: 'Calgary',

  // Not advertised on the landing page, kept because /events/miami has been a
  // working URL and removing it would turn any existing link into a dead end.
  // Either put Miami on the marketing page or retire the slug deliberately —
  // it should not stay in this in-between state forever.
  miami: 'Miami',

  // Short form kept working alongside dallas-fort-worth's full slug: it was in
  // CityEventsPage's map before this file existed, so it may be linked.
  dallas: 'Dallas',
});

/**
 * The slugs, as a Set.
 *
 * EventRouteResolver uses this to tell /events/houston from
 * /events/afrobeats-jollof-ab12, and passwordGateRules.js uses it to keep
 * organizers out of the city browse pages before launch. Both must agree about
 * what a city is, which is why neither keeps its own copy.
 */
export const CITY_SLUGS = new Set(Object.keys(CITIES));

/**
 * Slugs that resolve but are not the city's canonical URL.
 *
 * `dallas` and `dallas-fort-worth` render the same page for the same city. Both
 * have to keep working — the short one may already be linked — but only one of
 * them can be the address search engines index, or the two split the ranking
 * signal for Dallas between them and neither wins.
 */
export const CITY_ALIASES = Object.freeze({
  dallas: 'dallas-fort-worth',
});

/**
 * The slugs that belong in a sitemap: every city, each named once.
 *
 * This is what the server mirrors. Aliases are deliberately absent — listing a
 * duplicate of a page already in the sitemap is worse than not listing it.
 */
export const CANONICAL_CITY_SLUGS = Object.freeze(
  Object.keys(CITIES).filter((slug) => !CITY_ALIASES[slug])
);

/** The address a city should be indexed under. Returns the slug unchanged if it is already canonical. */
export function canonicalCitySlug(slug = '') {
  const key = String(slug).toLowerCase();
  return CITY_ALIASES[key] || key;
}

/** Display name for a slug, title-casing anything unrecognised. */
export function cityNameFromSlug(slug = '') {
  const key = String(slug).toLowerCase();
  if (CITIES[key]) return CITIES[key];
  return key
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}
